import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Popover, Stack } from '@mui/material';
import { Button, Typography, colors, Switch, Tooltip } from '@sweep-io/sweep-design';
import { Globe, Users, Info, CheckCircle } from '@sweep-io/sweep-design/dist/icons';
import { telemetry } from '../../../telemetry';
import { useAiAgentApiFacade } from '../../../apis/facades/useAiAgentApiFacade';
import { useArtifactsOptional } from './useArtifacts';

interface FeedbackMessageProps {
  title: string;
  description?: string;
  type: 'positive' | 'negative';
}

const FeedbackMessage = ({ title, description, type }: FeedbackMessageProps) => (
  <Stack
    sx={{
      backgroundColor: colors.blue[100],
      borderRadius: '8px',
      p: 1.5,
    }}
  >
    <Stack direction="row" alignItems="center" gap={1} mb={description ? 0.5 : 0}>
      {type === 'positive' ? (
        <CheckCircle size="small" color={colors.grey[800]} />
      ) : (
        <Info size="small" color={colors.grey[800]} />
      )}
      <Typography variant="caption-bold" color={colors.grey[900]}>
        {title}
      </Typography>
    </Stack>
    {description && (
      <Box>
        <Typography variant="caption" color={colors.grey[700]}>
          {description}
        </Typography>
      </Box>
    )}
  </Stack>
);

export interface ShareArtifactPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  artifact: {
    id: string;
    title: string;
  };
}

export const ShareArtifactPopover = ({
  anchorEl,
  onClose,
  artifact,
}: ShareArtifactPopoverProps) => {
  const [isShared, setIsShared] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const [artifactId, setArtifactId] = useState<string | null>(null);

  // Feedback messages
  const [shareMessage, setShareMessage] = useState<FeedbackMessageProps | null>(null);
  const [publishMessage, setPublishMessage] = useState<FeedbackMessageProps | null>(null);

  const { patch_share_artifact, patch_artifact_public, get_artifact_public_token, get_artifact } =
    useAiAgentApiFacade();
  const artifactsContext = useArtifactsOptional();

  const isOpen = Boolean(anchorEl);

  // Clear feedback messages when popover opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShareMessage(null);
      setPublishMessage(null);
    }
  }, [isOpen]);

  // Fetch artifact sharing status when popover opens
  useEffect(() => {
    const fetchArtifactStatus = async () => {
      if (!isOpen || !artifact.id) return;

      setIsLoading(true);
      try {
        const fetchedArtifact = await get_artifact(artifact.id);
        setIsShared(fetchedArtifact.isShared);
        setIsPublished(fetchedArtifact.isPublic);
        setArtifactId(fetchedArtifact.artifactId);
      } catch (error) {
        telemetry.captureError(error);
        setIsShared(false);
        setIsPublished(false);
        setArtifactId(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArtifactStatus();
  }, [isOpen, artifact.id, get_artifact]);

  // Fetch public token when artifact is published
  useEffect(() => {
    const fetchPublicToken = async () => {
      if (isPublished && artifact.id) {
        try {
          const { token } = await get_artifact_public_token(artifact.id);
          setPublicToken(token);
        } catch (error) {
          telemetry.captureError(error);
          setPublicToken(null);
        }
      } else {
        setPublicToken(null);
      }
    };

    fetchPublicToken();
  }, [isPublished, artifact.id, get_artifact_public_token]);

  const sharedUrl = useMemo(() => {
    return `${window.location.origin}/share-artifacts/${artifact.id}`;
  }, [artifact.id]);

  const publicUrl = useMemo(() => {
    if (!publicToken) return null;
    return `${window.location.origin}/public/artifacts/${artifact.id}?token=${publicToken}`;
  }, [artifact.id, publicToken]);

  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      telemetry.captureError(error);
    }
  }, []);

  const handleShareToggle = useCallback(async () => {
    setIsLoading(true);
    setShareMessage(null);
    try {
      const newIsShared = !isShared;
      const result = await patch_share_artifact(artifact.id, newIsShared);
      setIsShared(result.isShared);
      if (result.isShared) {
        setShareMessage({
          title: 'This artifact is now shared with your company.',
          type: 'positive',
        });
      } else {
        setShareMessage({
          title: 'This artifact is no longer shared.',
          description: 'Members who previously had access can no longer view it.',
          type: 'negative',
        });
      }
      // Update the artifacts context so other components (e.g., ArtifactPreview) see the change
      if (artifactId && artifactsContext) {
        artifactsContext.updateArtifactSharingStatus(artifactId, result.isShared, isPublished);
      }
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      setIsLoading(false);
    }
  }, [isShared, isPublished, artifact.id, artifactId, artifactsContext, patch_share_artifact]);

  const handlePublishToggle = useCallback(async () => {
    setIsLoading(true);
    setPublishMessage(null);
    try {
      const newIsPublished = !isPublished;
      const result = await patch_artifact_public(artifact.id, newIsPublished);
      setIsPublished(result.isPublic);
      if (result.isPublic) {
        setPublishMessage({
          title: 'The artifact has been successfully published.',
          type: 'positive',
        });
      } else {
        setPublishMessage({
          title: 'This artifact is no longer publicly accessible.',
          description: 'Visitors can no longer view it using the public link.',
          type: 'negative',
        });
      }
      // Update the artifacts context so other components (e.g., ArtifactPreview) see the change
      if (artifactId && artifactsContext) {
        artifactsContext.updateArtifactSharingStatus(artifactId, isShared, result.isPublic);
      }
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      setIsLoading(false);
    }
  }, [isShared, isPublished, artifact.id, artifactId, artifactsContext, patch_artifact_public]);

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            width: '448px',
            borderRadius: '8px',
            boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.12)',
            mt: 1,
          },
        },
      }}
    >
      <Stack p={3} gap={3}>
        <Typography variant="h3" color={colors.grey[900]}>
          Share Artifact
        </Typography>

        {/* Share with your company section */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Users size="medium" color={colors.grey[700]} />
              <Typography variant="body-bold" color={colors.grey[900]}>
                Share with your company
              </Typography>
            </Stack>
            <Tooltip
              title={
                isShared ? (
                  <>
                    Unshare this artifact.
                    <br />
                    Team members who previously had access will lose it
                  </>
                ) : (
                  'Share artifact'
                )
              }
              placement="top"
            >
              <span>
                <Switch checked={isShared} onChange={handleShareToggle} disabled={isLoading} />
              </span>
            </Tooltip>
          </Stack>
          <Typography variant="body" color={colors.grey[700]}>
            Team members with access can view this artifact.
          </Typography>

          {isShared && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap="8px"
              sx={{
                mt: 1.5,
                p: 1,
                backgroundColor: colors.grey[100],
                borderRadius: '4px',
                border: `1px solid ${colors.grey[300]}`,
              }}
            >
              <Box
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: colors.grey[500],
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                {sharedUrl}
              </Box>
              <Button
                startIconName="Copy"
                size="tiny"
                variant="system"
                onClick={() => handleCopyUrl(sharedUrl)}
              >
                Copy Link
              </Button>
            </Stack>
          )}

          {shareMessage && (
            <Box sx={{ mt: 1.5 }}>
              <FeedbackMessage {...shareMessage} />
            </Box>
          )}
        </Box>

        <Box sx={{ borderTop: `1px solid ${colors.grey[200]}` }} />

        {/* Publish artifact section */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Globe size="medium" color={colors.grey[700]} />
              <Typography variant="body-bold" color={colors.grey[900]}>
                Publish artifact
              </Typography>
            </Stack>
            <Tooltip
              title={
                isPublished ? (
                  <>
                    Remove public access.
                    <br />
                    Visitors will lose the ability to view this link
                  </>
                ) : (
                  'Publish artifact'
                )
              }
              placement="top"
            >
              <span>
                <Switch checked={isPublished} onChange={handlePublishToggle} disabled={isLoading} />
              </span>
            </Tooltip>
          </Stack>
          <Typography variant="body" color={colors.grey[700]}>
            Anyone with the link can view this artifact.
          </Typography>

          {isPublished && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap="8px"
              sx={{
                mt: 1.5,
                p: 1,
                backgroundColor: colors.grey[100],
                borderRadius: '4px',
                border: `1px solid ${colors.grey[300]}`,
              }}
            >
              {publicUrl ? (
                <>
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: colors.grey[500],
                      fontSize: '14px',
                      lineHeight: '20px',
                    }}
                  >
                    {publicUrl}
                  </Box>
                  <Button
                    startIconName="Copy"
                    size="tiny"
                    variant="system"
                    onClick={() => handleCopyUrl(publicUrl)}
                  >
                    Copy Link
                  </Button>
                </>
              ) : (
                <Typography variant="body" color={colors.grey[500]}>
                  Loading...
                </Typography>
              )}
            </Stack>
          )}

          {publishMessage && (
            <Box sx={{ mt: 1.5 }}>
              <FeedbackMessage {...publishMessage} />
            </Box>
          )}
        </Box>
      </Stack>
    </Popover>
  );
};
